import _ from 'lodash';
import * as devices from '../../../modules/devices';
import { addToast } from '../../../modules/core/actions';
import { ToastType } from '../../../modules/core/constants';
import { RootScopeEvents } from 'common/services/root-scope-types';
import { updateGrowthBookIndentifiers } from '../../../global/global.experiments';

export default angular
  .module('doxy.patient.common.patientService', [])
  .factory(
    'patientService',
    function (
      $timeout,
      $rootScope,
      $stateParams,
      $translate,
      Browser,
      UserLocationDetails,
      institutionHelper,
      Room,
      User,
      patientRealtimeActions,
      globalUi,
      pn,
      VideoProviderFactory,
      activeComponents,
      opentokTestService,
      $ngRedux,
      roomHelpersFactory,
      routerHelperFactory,
      trackingFactory
    ) {
      let realtimeActionsInitialised = false;

      function getLocationDetails() {
        return UserLocationDetails.getLocationDetails();
      }

      function getInstitution() {
        return institutionHelper.getCurrentInstitution();
      }

      function getCurrentTime() {
        return User.getCurrentTime().$promise.then(
          (result) => result.ts,
          (err) => new Date().getTime()
        );
      }

      function showSuccessfulTransferToast() {
        const shouldShowTransferToast = localStorage.getItem(
          'should-show-transfer-toast'
        );
        if (shouldShowTransferToast) {
          $ngRedux.dispatch(
            addToast(ToastType.success, 'All set. Please wait here.')
          );
          localStorage.removeItem('should-show-transfer-toast');
        }
      }

      function checkPasscode(room, passcode = '') {
        if (!room.usePasscode) return Promise.resolve();
        return Room.prototype$isValidPasscode(
          { id: room.id },
          { passcode }
        ).$promise.then((result) => {
          if (!result.isValid) {
            return Promise.reject({
              code: 'INCORRECT_PASSCODE',
              message: $translate.instant('notification.wrongPasscode'),
            });
          }

          return Promise.resolve(result.isValid);
        });
      }

      function notifyTransferSenderAboutSuccessfulTransfer(roomId) {
        const transferSenderId = localStorage.getItem('transfer-sender-id');
        const patientTransferNotifyKey = localStorage.getItem(
          'patient-transfer-notify-key'
        );
        if (transferSenderId && patientTransferNotifyKey) {
          Room.prototype$sendChatNotificationAboutSuccessfulTransfer(
            { id: roomId },
            {
              userId: transferSenderId,
              senderNotifyKey: patientTransferNotifyKey,
            }
          ).$promise.then(() => {
            localStorage.removeItem('transfer-sender-id');
            localStorage.removeItem('patient-transfer-notify-key');
          });
        }
      }

      // patient = { username, passcode = '' }
      function checkInAsPatient({ patient, room, fromRoomId }) {
        return checkPasscode(room, patient.passcode)
          .then(() => {
            return Promise.all([
              getLocationDetails(),
              getInstitution(),
              getCurrentTime(),
            ]);
          })
          .then(([locationDetails, institution, checkedInAt]) => {
            roomHelpersFactory.setRooms([room]);
            updateGrowthBookIndentifiers({ institution });
            const deviceInfo = devices.selectors.getAllDevicesBooleanProps(
              $ngRedux.getState()
            );
            if (activeComponents.chat) activeComponents.chat.reset();

            if (!realtimeActionsInitialised) {
              patientRealtimeActions.start();
              realtimeActionsInitialised = true;
            }

            const userState = {
              connectionLevel: undefined,
              username: patient.username,
              status: 'available',
              mobileBrowser: Browser.isHandheld(),
              onlyChat: !deviceInfo.hasMediaAccess || !Browser.isSupported(),
              hasCamera: deviceInfo.hasCamera,
              hasMic: deviceInfo.hasMicrophone,
              hasCameraAccess: deviceInfo.hasCameraAccess,
              hasMicrophoneAccess: deviceInfo.hasMicrophoneAccess,
              privateData: {
                ip: locationDetails.clientIp,
                location: locationDetails.location,
              },
              system: Browser.getSystemInfo(),
              checkedInAt,
              vcp: VideoProviderFactory.activeProviderName,
              token: $stateParams.token,
            };

            if ($stateParams.pid) {
              userState.pid = $stateParams.pid;
            }

            return pn
              .initPatient(room.id, userState, patient.passcode)
              .then(() => {
                if (deviceInfo.hasMicrophoneAccess) {
                  opentokTestService.initNetworkTest({
                    audioOnly: !deviceInfo.hasCameraAccess,
                  });
                  $rootScope.$on(
                    'opentokTestService:qualityResults',
                    (e, data) => {
                      pn.setState({
                        connection: {
                          level: data.estimation,
                        },
                      });
                    }
                  );
                }

                const anonymousId = trackingFactory.segment.getAnonymousId();
                const platformData = Browser.getPlatformData();
                const hasTransferPatientAccess =
                  institution &&
                  institution.institutionAccess &&
                  institution.institutionAccess[
                    'clinicFeatures_transferPatients'
                  ];

                if (hasTransferPatientAccess && fromRoomId) {
                  Room.prototype$notifyMembersAboutTransfer(
                    { id: room.id },
                    {
                      name: patient.username,
                      anonymousId,
                      fromRoomId,
                      platformData,
                    }
                  ).$promise.catch((err) => {
                    console.error(
                      'Room.prototype$notifyMembersAboutPatient error:',
                      err
                    );
                  });
                } else {
                  Room.prototype$notifyMembersAboutPatient(
                    { id: room.id },
                    { name: patient.username, anonymousId, platformData }
                  ).$promise.catch((err) => {
                    console.error(
                      'Room.prototype$notifyMembersAboutPatient error:',
                      err
                    );
                  });
                }

                $rootScope.$emit(
                  RootScopeEvents.CHECK_IN_SUCCESS,
                  pn.globalState
                );

                Room.prototype$onCheckIn(
                  { id: room.id },
                  {
                    name: userState.username,
                    pid: userState.pid,
                    ts: userState.checkedInAt,
                    url: routerHelperFactory.getBaseUrl(),
                  }
                ).$promise.catch((err) => {
                  console.error('Room.prototype$onCheckIn error:', err);
                });

                $timeout(() => {
                  globalUi.ui.checkIn = false;
                  globalUi.ui.patientCheckIn = false;
                }, globalUi.PATIENT_CHECK_IN_CLOSE_POPUP_DELAY);
              });
          });
      }

      return {
        checkInAsPatient,
        checkPasscode,
        showSuccessfulTransferToast,
        notifyTransferSenderAboutSuccessfulTransfer,
      };
    }
  ).name;
